import { Injectable } from '@angular/core';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import {
  DataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Extenstions,
  GeoDsDocumentViewModel,
  IApiResult,
  IDeleteConditions,
} from '../documents-editor/documents-list/document.editor.models';
import { DocumentKind, GeoDsDocument } from '../models/geods-document.model';
import { Person } from '../models/person.model';

@Injectable()
export class DocumentService {
  private documentObjectType = 'Document';
  private personObjectType = 'Person';
  public videoExtensions = ['mp4'];
  private previewExtensions = ['pdf', 'mp4', ...Extenstions.images];
  private dataToUpload: Subject<GeoDsDocument> = new Subject();
  public dataToUpload$: Observable<GeoDsDocument> = this.dataToUpload.asObservable();

  constructor(
    private persistenceService: PersistenceService,
    private coreDataService: DataService,
    private abcCore: AbcCore,
  ) {}

  public updateDocumentsList(data: GeoDsDocument): void {
    this.dataToUpload.next(data);
  }

  public onFileChanged(eventFiles: any[]): void {
    const doc = new GeoDsDocument(null);
    let documentSource = '';
    if (eventFiles.length !== 0) {
      const files: any[] = Array.from(eventFiles);
      files.forEach((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(new Blob([file]));
        reader.onloadend = () => {
          const fileNameArray = file?.name.split('.');
          const extension = fileNameArray.slice(-1)[0];
          const timestamp = new Date().getTime();
          const fileName = file?.name ?? '--';
          documentSource = reader.result as string;
          doc.Object = documentSource;
          doc.FileName = fileName;
          doc.Designation = `${fileName}-${timestamp.toString()}`;
          doc.Size = file['size'] / 1000 + ' KB';
          doc.Kind = this.getKind(extension);

          this.dataToUpload.next({ ...doc });
        };
      });
    }
  }

  public getDocuments(
    opath: string,
    withObject = true,
    limitCount?: number,
    additionalColumns: QueryColumn[] = [],
  ): Observable<GeoDsDocument[]> {
    const documentColumns: QueryColumn[] = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Designation', 'Designation', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('FileName', 'FileName', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('ParentId', 'ParentId', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Kind', 'Kind', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('IdRefPersonInsert', 'IdRefPersonInsert', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Reserve2', 'Size', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('SysDateInsert', 'SysDateInsert', QueryColumnSortOrder.Descending),
      this.coreDataService.createQueryColumn('SysTimeInsert', 'SysTimeInsert'),
      this.coreDataService.createQueryColumn('InsertDate', 'InsertDate', QueryColumnSortOrder.Descending),
      this.coreDataService.createQueryColumn('DateEnd', 'DateEnd'),
      ...additionalColumns,
    ];

    if (withObject) {
      documentColumns.push(this.coreDataService.createQueryColumn('Object', 'Object', QueryColumnSortOrder.None));
    }

    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = this.documentObjectType;
    mainQuery.Columns = documentColumns;
    mainQuery.OPath = opath;

    if (limitCount) {
      mainQuery.LimitCount = limitCount;
    }

    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService
      .executeReadObjectsQuery(readObjectQuery)
      .pipe(map((docs) => ConvertHelper.arrayBufferToObjectArray(docs)));
  }

  public prepareProfile(userName): Observable<Person[]> {
    const profileOPath = 'Login=' + "'" + userName.userName + "'";
    return this.getUsers(profileOPath);
  }

  public getUsers(opath?: string): Observable<Person[]> {
    const personsColumns: QueryColumn[] = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('FirstName', 'FirstName', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('LastName', 'LastName', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('EMail', 'EMail', QueryColumnSortOrder.None),
    ];

    const readObjectQuery: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = this.personObjectType;
    mainQuery.Columns = personsColumns;

    if (opath) {
      mainQuery.OPath = opath;
    }

    readObjectQuery.ObjectQueries = [mainQuery];
    return this.coreDataService
      .executeReadObjectsQuery(readObjectQuery)
      .pipe(map((personsArrayBuffer) => ConvertHelper.arrayBufferToObjectArray(personsArrayBuffer)));
  }

  public deleteDocumentService(id: string): Observable<IApiResult> {
    const persistQuery: PersistObjectModel = new PersistObjectModel();
    const targetObject: TargetObjectData = new TargetObjectData();
    targetObject.Mode = PersistMode.Delete;
    targetObject.ObjectKey = new ObjectKey();
    targetObject.ObjectKey.Id = id;
    targetObject.ObjectKey.ObjectType = this.documentObjectType;
    persistQuery.Object = targetObject;
    return this.coreDataService
      .executePersistObjectQuery(persistQuery)
      .pipe(map((deletedDocs) => ConvertHelper.arrayBufferToObject(deletedDocs)));
  }

  public deleteMultipleDocumentService(ids: string[]): Observable<IApiResult[][]> {
    const query: PersistObjectsModel = new PersistObjectsModel();
    const persistQuery: TargetObjectData[][] = [[]];

    ids.forEach((id) => {
      const targetObject: TargetObjectData = new TargetObjectData();
      targetObject.Mode = PersistMode.Delete;
      targetObject.ObjectKey = new ObjectKey();
      targetObject.ObjectKey.Id = id;
      targetObject.ObjectKey.ObjectType = this.documentObjectType;
      persistQuery[0].push(targetObject);
    });

    query.Objects = persistQuery;
    return this.persistenceService
      .executePersistObjectsQuery(query)
      .pipe(map((deletedDocs) => ConvertHelper.arrayBufferToObjectArray(deletedDocs)));
  }

  public archiveMultipleDocumentService(ids: string[]): Observable<IApiResult[][]> {
    const query: PersistObjectsModel = new PersistObjectsModel();
    const persistQuery: TargetObjectData[][] = [[]];

    ids.forEach((id) => {
      const targetObject: TargetObjectData = new TargetObjectData();
      targetObject.Mode = PersistMode.Update;
      targetObject.ObjectKey = new ObjectKey();
      targetObject.ObjectKey.ObjectType = this.documentObjectType;
      targetObject.ObjectKey.Id = id;
      const documentColumns: TargetColumnValue[] = [{ Name: 'Reserve3', Value: 'IsArchived' }];
      targetObject.TargetColumns = documentColumns;
      persistQuery[0].push(targetObject);
    });

    query.Objects = persistQuery;
    return this.persistenceService
      .executePersistObjectsQuery(query)
      .pipe(map((archiveDocs) => ConvertHelper.arrayBufferToObjectArray(archiveDocs)));
  }

  public persistUploadedFiles(
    documents: GeoDsDocument[],
    additionalDocumentColumns?: Map<string, string | number | boolean>,
  ): Observable<IApiResult[][]> {
    const query: PersistObjectsModel = new PersistObjectsModel();
    const filesPersistQuery: TargetObjectData[][] = [[]];
    let documentColumns: TargetColumnValue[] = [];
    documents.forEach((document) => {
      const fileInsertObjectData: TargetObjectData = new TargetObjectData();
      fileInsertObjectData.Mode = PersistMode.Insert;
      fileInsertObjectData.ObjectKey = new ObjectKey();
      fileInsertObjectData.ObjectKey.ObjectType = this.documentObjectType;
      documentColumns = [
        {
          Name: 'Object',
          Value: this.getBinaryStringFromUrl(document?.Object),
        },
        { Name: 'Designation', Value: document?.Designation },
        { Name: 'FileName', Value: document?.FileName },
        { Name: 'ParentId', Value: document?.ParentId },
        { Name: 'Kind', Value: document?.Kind },
        { Name: 'ParentObjectType', Value: document?.ParentObjectType },
        { Name: 'IdRefPersonInsert', Value: document?.IdRefPersonInsert },
        { Name: 'InsertDate', Value: new Date() },
        { Name: 'Reserve2', Value: document?.Size },
      ];
      fileInsertObjectData.TargetColumns = documentColumns;
      filesPersistQuery[0].push(fileInsertObjectData);
    });
    if (additionalDocumentColumns) {
      additionalDocumentColumns.forEach((value, key) => {
        documentColumns.push({ Name: key, Value: value });
      });
    }
    query.Objects = filesPersistQuery;
    return this.persistenceService
      .executePersistObjectsQuery(query)
      .pipe(map((persistedDocArrayBuffer) => ConvertHelper.arrayBufferToObjectArray(persistedDocArrayBuffer)));
  }

  private getBinaryStringFromUrl(url: string): string {
    return url?.split(',')[1];
  }

  //TODO add remaining extensions
  private getKind(extension: string): DocumentKind {
    if (Extenstions.images.includes(extension.toLowerCase())) {
      return DocumentKind.Bild;
    } else if (Extenstions.videos.includes(extension.toLowerCase())) {
      return DocumentKind.Video;
    } else {
      return DocumentKind.Datei;
    }
  }

  public downloadFile(doc: GeoDsDocument): void {
    const a = document.createElement('a');
    const mimeType = this.getMimeType(doc.FileName);
    a.href = `data:${mimeType};base64,${doc.Object}`;
    a.download = doc?.FileName || doc?.Designation;
    a.click();
  }

  public previewFile(doc: GeoDsDocument): void {
    if (doc.Kind === DocumentKind.Datei) {
      const pdfWindow = this.abcCore.openWindow('');
      pdfWindow?.document.write(
        "<iframe width='100%' height='100%' src='data:application/pdf;base64, " + encodeURI(doc.Object) + "'></iframe>",
      );
    } else {
      const image = new Image();
      image.src = `data:${this.getMimeType(doc.FileName)};base64,` + doc.Object;
      const window = this.abcCore.openWindow('');
      window?.document.write(image.outerHTML);
    }
  }

  public getMimeType(fileName: string): string {
    const _extension = fileName && fileName.includes('.') ? fileName.split('.').pop() : '';
    const extension = _extension.toLowerCase();

    switch (extension) {
      case 'jfif':
        return 'image/jfif';
      case 'png':
        return 'image/png';
      case 'jpg':
        return 'image/jpeg';
      case 'svg':
        return 'image/svg+xml';
      case 'pdf':
      case 'zip':
        return 'application/' + extension;
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'ppt':
        return 'application/vnd.ms-powerpoint';
      case 'pptx':
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'txt':
        return 'text/plain';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'csv':
        return 'text/csv';
      case 'webp':
        return 'image/webp';
      case 'mp4':
        return 'video/mp4';
      default:
        return '';
    }
  }

  public convertDocumentsToDocumentsViewModels(
    documents: GeoDsDocument[],
    allowPreview: boolean,
    allowDownload: boolean,
    allowDetails: boolean,
    deleteConditions: IDeleteConditions,
  ): Observable<GeoDsDocumentViewModel[]> {
    let res: Observable<Person[]>;
    if (documents && documents.length > 0) {
      const uniqueIdRefPersonInsert = documents
        .map((item) => item.IdRefPersonInsert)
        .filter((value, index, current_value) => current_value.indexOf(value) === index);
      const opath = 'Id in ' + `(${uniqueIdRefPersonInsert.map((Id) => "'" + Id + "'")})`;
      res = this.getUsers(opath);
    } else {
      res = of([]);
    }

    return res.pipe(
      map((users) => {
        return documents.map((doc) => {
          const creator = users.filter((user) => user.Id === doc.IdRefPersonInsert)[0];

          return {
            ...doc,
            IdRefPersonInsertFullName: creator?.FirstName
              ? `${creator?.FirstName ?? '--'} ${creator?.LastName ?? '--'}`
              : '',
            IdRefPersonInsertEmail: creator?.EMail ? creator?.EMail : '',
            allowPreview: this.allowPreviewFile(doc?.FileName, allowPreview),
            allowDelete: this.returnDeleteVisibilityCondition(doc, deleteConditions),
            allowDownload: this.allowDownloadFile(doc?.FileName, allowDownload),
            allowDetails: allowDetails,
          };
        });
      }),
    );
  }

  // ------------------- actions conditions

  private combineDates(date1: Date, date2: Date): Date {
    const day = date1.getDate();
    const month = date1.getMonth();
    const year = date1.getFullYear();
    const hours = date2.getHours();
    const minutes = date2.getMinutes();

    return new Date(year, month, day, hours, minutes);
  }

  public calculateDateDifferenceInHours(
    docInsertedDate: string,
    docInsertedTime: string,
    date2: Date = new Date(),
  ): number {
    const dateFromInsertedDate = new Date(docInsertedDate);
    const dateFromInsertedTime = new Date(docInsertedTime);

    const date1 = this.combineDates(dateFromInsertedDate, dateFromInsertedTime);

    const timeDifferenceMilliseconds = date2.getTime() - date1.getTime();

    const seconds = Math.floor(timeDifferenceMilliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    return hours;
  }

  public allowDownloadFile(fileName: string, allowDownload: boolean): boolean {
    if (fileName?.trim()) {
      const fileExtension = this.getMimeType(fileName);
      return allowDownload && fileExtension.length > 0;
    } else {
      return false;
    }
  }

  public returnDeleteVisibilityCondition(docData: GeoDsDocument, deleteConditions: IDeleteConditions): boolean {
    if (docData) {
      return (
        (deleteConditions.allowDelete &&
          !deleteConditions.deactiveDeleteAfterSpecificTime &&
          !deleteConditions.allowDeleteOnlyToCreator) ||
        (deleteConditions.allowDelete &&
          !deleteConditions.allowDeleteOnlyToCreator &&
          deleteConditions.deactiveDeleteAfterSpecificTime &&
          this.calculateDateDifferenceInHours(docData.SysDateInsert.toString(), docData.SysTimeInsert.toString()) <
            deleteConditions.TimeInHoursToDeactivateDelete) ||
        (deleteConditions.allowDelete &&
          !deleteConditions.deactiveDeleteAfterSpecificTime &&
          deleteConditions.allowDeleteOnlyToCreator &&
          docData.IdRefPersonInsert === deleteConditions.userId) ||
        (deleteConditions.allowDelete &&
          deleteConditions.deactiveDeleteAfterSpecificTime &&
          deleteConditions.allowDeleteOnlyToCreator &&
          this.calculateDateDifferenceInHours(docData.SysDateInsert.toString(), docData.SysTimeInsert.toString()) <
            deleteConditions.TimeInHoursToDeactivateDelete &&
          docData.IdRefPersonInsert === deleteConditions.userId)
      );
    } else {
      return false;
    }
  }

  public allowPreviewFile(fileName: string, allowPreview: boolean): boolean {
    if (fileName?.trim()) {
      const fileNameToArray = fileName ? fileName.split('.') : '';
      const extension = fileNameToArray ? fileNameToArray.slice(-1)[0] : '';
      return allowPreview && extension && this.previewExtensions.includes(extension.toLowerCase());
    } else {
      return false;
    }
  }
  public archiveDocument(documentId: string): Observable<any> {
    const documentPersistQuery: TargetObjectData = new TargetObjectData();
    documentPersistQuery.Mode = PersistMode.Update;
    documentPersistQuery.ObjectKey = new ObjectKey();
    documentPersistQuery.ObjectKey.ObjectType = 'Document';
    documentPersistQuery.ObjectKey.Id = documentId;
    const documentColumns: TargetColumnValue[] = [{ Name: 'Reserve3', Value: 'IsArchived' }];
    documentPersistQuery.TargetColumns = documentColumns;
    const commentQuery: PersistObjectModel = new PersistObjectModel();
    commentQuery.Object = documentPersistQuery;
    return this.coreDataService.executePersistObjectQuery(commentQuery);
  }

  public updateDocument(documentId: string, document: GeoDsDocument): Observable<any> {
    const documentPersistQuery: TargetObjectData = new TargetObjectData();
    const persistQuery: TargetObjectData[][] = [[]];
    documentPersistQuery.Mode = PersistMode.Update;
    documentPersistQuery.ObjectKey = new ObjectKey();
    documentPersistQuery.ObjectKey.ObjectType = this.documentObjectType;
    documentPersistQuery.ObjectKey.Id = documentId;
    const documentColumns: TargetColumnValue[] = [
      {
        Name: 'Object',
        Value: document?.Object,
      },
      { Name: 'FileName', Value: document?.FileName },
      { Name: 'Comment', Value: document?.Comment },
    ];
    documentPersistQuery.TargetColumns = documentColumns;
    persistQuery[0].push(documentPersistQuery);
    const updateDocumentQuery: PersistObjectModel = new PersistObjectModel();
    updateDocumentQuery.Object = documentPersistQuery;
    return this.coreDataService.executePersistObjectQuery(updateDocumentQuery);
  }
}
